import {z} from 'zod'

export const BugReportFormSchema = z.object({
    title: z.string().min(1, 'Title is required'),
    description: z.string().optional().default('')
})

export type BugReportFormValues = z.infer<typeof BugReportFormSchema>

export type EnvironmentSnapshot = {
    reporterName: string
    reporterEmail: string
    url: string
    browser: string
    os: string
    screenResolution: string
    viewportSize: string
    appVersion: string
    timestamp: string
}

/**
 * A single attachment reference, as returned by the backend after a successful
 * upload and as sent inside the create-issue payload.
 */
export const BugReportAttachmentRef = z.object({
    assetUrl: z.string(),
    filename: z.string(),
    contentType: z.string()
})

export type BugReportAttachmentRef = z.infer<typeof BugReportAttachmentRef>

export type BugReportIssueCreatePayload = {
    title: string
    description: string
    environment: EnvironmentSnapshot
    attachments: BugReportAttachmentRef[]
}

export const CreatedBugReportIssue = z.object({
    id: z.string(),
    identifier: z.string(),
    title: z.string(),
    url: z.string()
})

export type CreatedBugReportIssue = z.infer<typeof CreatedBugReportIssue>

export const BugReportIssueStateType = z.union([
    z.literal('triage'),
    z.literal('backlog'),
    z.literal('unstarted'),
    z.literal('started'),
    z.literal('completed'),
    z.literal('canceled')
])

export type BugReportIssueStateType = z.infer<typeof BugReportIssueStateType>

export const BugReportIssueState = z.object({
    name: z.string(),
    color: z.string(),
    type: BugReportIssueStateType
})

export type BugReportIssueState = z.infer<typeof BugReportIssueState>

export const BugReportIssue = z.object({
    id: z.string(),
    identifier: z.string(),
    title: z.string(),
    description: z.string().nullable(),
    url: z.string(),
    createdAt: z.string(),
    state: BugReportIssueState.nullable()
})

export type BugReportIssue = z.infer<typeof BugReportIssue>

export const BugReportIssueList = z.array(BugReportIssue)

export type BugReportIssueList = z.infer<typeof BugReportIssueList>

/**
 * Client-local state for one attachment as it moves through the dropzone
 * lifecycle. Never sent over the wire — only the `done` entries are projected
 * into `BugReportAttachmentRef[]` at submit time.
 */
export type AttachmentUploadStatus = 'pending' | 'uploading' | 'done' | 'error'

export type AttachmentUploadState = {
    id: string
    file: File
    status: AttachmentUploadStatus
    assetUrl: string | null
    error: string | null
}
